import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, signInWithProvider, signOut as supabaseSignOut, onAuthStateChange } from '../utils/supabase';
import { getGamificationSystem, migrateGamificationData } from '../utils/gamificationSystem.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [gamificationSystem, setGamificationSystem] = useState(null);

  // 게이미피케이션 시스템 초기화
  const initializeGamificationSystem = (userData) => {
    if (userData && userData.id) {
      const gamificationInstance = getGamificationSystem(userData.id);
      setGamificationSystem(gamificationInstance);
      
      // 소셜 로그인 사용자에게 특별 배지 지급
      if (userData.provider && userData.provider !== 'guest') {
        gamificationInstance.awardBetaBadge();
        
        // 소셜 로그인 보너스 포인트 (첫 로그인 시에만)
        const stats = gamificationInstance.getStats();
        if (stats.totalCorrectAnswers === 0 && stats.points < 50) {
          gamificationInstance.addPoints('BETA_PARTICIPATION');
        }
      }
      
      return gamificationInstance;
    }
    return null;
  };

  // Supabase 사용자를 내부 사용자 형태로 변환
  const transformSupabaseUser = (supabaseUser) => {
    if (!supabaseUser) return null;

    // OAuth identity에서 제공자 정보 추출
    const identity = supabaseUser.identities && supabaseUser.identities.length > 0 
      ? supabaseUser.identities[0] 
      : null;
    
    const provider = identity ? identity.provider : 'email';
    const providerData = identity ? identity.identity_data : {};

    return {
      id: supabaseUser.id,
      name: providerData.full_name || providerData.name || supabaseUser.user_metadata?.name || supabaseUser.email,
      email: supabaseUser.email,
      provider: provider,
      profilePicture: providerData.avatar_url || providerData.picture || supabaseUser.user_metadata?.avatar_url,
      isGuest: false,
      createdAt: supabaseUser.created_at
    };
  };

  // 인증 상태 변경 처리
  useEffect(() => {
    // 초기 세션 확인
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        console.log('🔍 Initial session check:', session);
        
        if (session?.user) {
          console.log('✅ Found initial session, user:', session.user);
          const transformedUser = transformSupabaseUser(session.user);
          console.log('✅ Transformed user:', transformedUser);
          setUser(transformedUser);
          setIsAuthenticated(true);
          localStorage.setItem('cbtUser', JSON.stringify(transformedUser));
          initializeGamificationSystem(transformedUser);
        } else {
          console.log('❌ No initial session found');
        }
      } catch (error) {
        console.error('❌ Failed to get initial session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getInitialSession();

    // 인증 상태 변경 리스너
    const { data: { subscription } } = onAuthStateChange((event, session) => {
      console.log('🔄 Auth state changed:', event, session);
      
      if (session?.user) {
        console.log('✅ User authenticated:', session.user);
        const transformedUser = transformSupabaseUser(session.user);
        console.log('✅ Transformed user:', transformedUser);
        
        // 기존 게스트 사용자가 있는 경우 데이터 마이그레이션
        if (user && user.isGuest && !transformedUser.isGuest) {
          console.log('🔄 Migrating gamification data from guest to authenticated user');
          migrateGamificationData(user.id, transformedUser.id);
        }
        
        setUser(transformedUser);
        setIsAuthenticated(true);
        localStorage.setItem('cbtUser', JSON.stringify(transformedUser));
        initializeGamificationSystem(transformedUser);
      } else {
        console.log('❌ No session or user logged out');
        setUser(null);
        setIsAuthenticated(false);
        setGamificationSystem(null);
        localStorage.removeItem('cbtUser');
      }
    });

    // 관리자 세션 복원
    const savedAdminUser = localStorage.getItem('adminAuth');
    if (savedAdminUser) {
      const adminData = JSON.parse(savedAdminUser);
      setAdminUser(adminData);
      setIsAdmin(true);
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // OAuth 콜백 처리 및 수동 세션 확인
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlFragment = new URLSearchParams(window.location.hash.slice(1));
      
      // URL에 OAuth 관련 파라미터가 있는지 확인
      const hasAuthParams = urlParams.get('auth') || urlFragment.get('access_token') || urlFragment.get('refresh_token');
      
      if (hasAuthParams) {
        console.log('🔄 OAuth 콜백 감지됨, 세션 확인 중...');
        
        // 약간의 지연 후 세션 확인 (Supabase 처리 시간 고려)
        setTimeout(async () => {
          try {
            // 1. URL에서 직접 토큰 확인
            const accessToken = urlFragment.get('access_token');
            const refreshToken = urlFragment.get('refresh_token');
            console.log('🔍 URL 토큰 확인:', { accessToken: !!accessToken, refreshToken: !!refreshToken });
            
            // 2. Supabase에서 세션 확인
            const { data: { session }, error } = await supabase.auth.getSession();
            console.log('🔍 OAuth 콜백 후 세션 확인:', session, error);
            
            // 3. 토큰이 있다면 수동으로 세션 설정 시도
            if (accessToken && !session) {
              console.log('🔧 수동 세션 설정 시도...');
              const { data: { session: newSession }, error: setError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
              });
              console.log('🔧 수동 세션 설정 결과:', newSession, setError);
              
              if (newSession?.user) {
                console.log('✅ 수동 OAuth 로그인 성공:', newSession.user);
                const transformedUser = transformSupabaseUser(newSession.user);
                setUser(transformedUser);
                setIsAuthenticated(true);
                localStorage.setItem('cbtUser', JSON.stringify(transformedUser));
                initializeGamificationSystem(transformedUser);
                return;
              }
            }
            
            // 4. 기본 세션 처리
            if (session?.user) {
              console.log('✅ OAuth 로그인 성공:', session.user);
              const transformedUser = transformSupabaseUser(session.user);
              setUser(transformedUser);
              setIsAuthenticated(true);
              localStorage.setItem('cbtUser', JSON.stringify(transformedUser));
              initializeGamificationSystem(transformedUser);
            } else {
              console.log('❌ OAuth 콜백 후에도 세션 없음');
            }
          } catch (error) {
            console.error('❌ OAuth 콜백 처리 오류:', error);
          }
        }, 1000);
        
        // URL에서 인증 파라미터 제거
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    handleOAuthCallback();
  }, []);

  // 게스트 로그인 (임시 사용자로 체험)
  const loginAsGuest = () => {
    const guestUser = {
      id: `guest_${Date.now()}`,
      name: '게스트 사용자',
      email: null,
      provider: 'guest',
      type: 'guest',
      isGuest: true,
      createdAt: new Date().toISOString()
    };
    
    setUser(guestUser);
    setIsAuthenticated(true);
    localStorage.setItem('cbtUser', JSON.stringify(guestUser));
    
    // 게이미피케이션 시스템 초기화
    initializeGamificationSystem(guestUser);
    
    return guestUser;
  };

  // 소셜 로그인 (Supabase OAuth)
  const loginWithSocial = async (provider) => {
    try {
      if (provider === 'google' || provider === 'kakao') {
        const { data, error } = await signInWithProvider(provider);
        
        if (error) {
          if (provider === 'kakao') {
            throw new Error('카카오 로그인이 현재 준비 중입니다. 구글 로그인이나 게스트 로그인을 이용해 주세요!');
          } else {
            throw new Error(`${provider} 로그인 중 오류가 발생했습니다. 다시 시도해 주세요.`);
          }
        }
        
        // OAuth 리다이렉션이 발생하므로 여기서는 추가 처리 불필요
        return;
      }

      if (provider === 'naver') {
        throw new Error('네이버 로그인은 현재 준비 중입니다. 구글/카카오 로그인이나 게스트 로그인을 이용해 주세요!');
      }

      throw new Error(`지원하지 않는 로그인 제공자: ${provider}`);
      
    } catch (error) {
      console.error('Social login failed:', error);
      throw error;
    }
  };

  // 관리자 로그인
  const adminLogin = (adminData) => {
    setAdminUser(adminData);
    setIsAdmin(true);
    localStorage.setItem('adminAuth', JSON.stringify(adminData));
  };

  // 관리자 로그아웃
  const adminLogout = () => {
    setAdminUser(null);
    setIsAdmin(false);
    localStorage.removeItem('adminAuth');
  };

  // 사용자 진행률 저장
  const saveUserProgress = (data) => {
    if (isAuthenticated && user) {
      const progressKey = `progress_${user.id}`;
      const existingProgress = JSON.parse(localStorage.getItem(progressKey) || '{}');
      const updatedProgress = { ...existingProgress, ...data };
      localStorage.setItem(progressKey, JSON.stringify(updatedProgress));
    }
  };

  // 사용자 진행률 로드
  const loadUserProgress = () => {
    if (isAuthenticated && user) {
      const progressKey = `progress_${user.id}`;
      return JSON.parse(localStorage.getItem(progressKey) || '{}');
    }
    return {};
  };

  // 로그아웃
  const logout = async () => {
    try {
      // 게스트 사용자인지 확인
      const isGuest = user?.id?.startsWith('guest_');
      
      if (isGuest) {
        // 게스트 사용자는 Supabase 로그아웃 없이 로컬 상태만 정리
        console.log('게스트 사용자 로그아웃');
        setUser(null);
        setIsAuthenticated(false);
        setGamificationSystem(null);
        localStorage.removeItem('cbtUser');
        localStorage.removeItem('userProgress');
      } else {
        // 일반 사용자는 Supabase 로그아웃
        await supabaseSignOut();
        // 클라이언트 상태는 onAuthStateChange에서 자동으로 정리됨
      }
    } catch (error) {
      console.error('Logout failed:', error);
      
      // 오류 발생 시 로컬 상태만 정리
      setUser(null);
      setIsAuthenticated(false);
      setGamificationSystem(null);
      localStorage.removeItem('cbtUser');
      localStorage.removeItem('userProgress');
    }
  };

  // 사용자 정보 새로고침 (호환성을 위해 유지)
  const fetchUserInfo = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const transformedUser = transformSupabaseUser(currentUser);
        setUser(transformedUser);
        setIsAuthenticated(true);
        return transformedUser;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      return null;
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    adminUser,
    isAdmin,
    isBetaUser: true, // 베타 제한 해제
    gamificationSystem,
    loginAsGuest,
    loginWithSocial,
    adminLogin,
    adminLogout,
    logout,
    saveUserProgress,
    loadUserProgress,
    fetchUserInfo
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};